import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getThreadId } from './sprouts-agent.action';
import { messageType } from './types';

export type IInitialState = {
  isAgentSpeaking?: boolean;
  audioVisualizerOpen?: boolean;
  threadId: string;
  loading: boolean;
  messageGenerating: boolean;
  messages: messageType[];
  chatStreaming: boolean;
  checkpoint: string;
  playbookInputFound: boolean;
  welcomeVoice: boolean;
};

const initialState: IInitialState = {
  isAgentSpeaking: false,
  audioVisualizerOpen: false,
  threadId: '',
  loading: false,
  messageGenerating: false,
  messages: [],
  chatStreaming: false,
  checkpoint: '',
  playbookInputFound: false,
  welcomeVoice: false,
};

const SproutsAgentSlice = createSlice({
  name: 'sproutsAgent',
  initialState,
  reducers: {
    setIsAgentSpeaking: (state, action: PayloadAction<boolean>) => {
      state.isAgentSpeaking = action.payload;
    },
    toggleAudioVisualizer: (state, action) => {
      state.audioVisualizerOpen = action.payload;
    },
    setMessageGenerating: (state, action) => {
      state.messageGenerating = action.payload;
    },
    addNewMessage: (state, action) => {
      const newMessage: messageType = {
        content: action.payload.content,
        role: action.payload.role,
        contentType: action.payload.contentType,
      };
      state.messages.push(newMessage);
    },
    appendMessage: (state, action) => {
      const updatedMessages = [...state.messages];
      const latestMessage = updatedMessages[updatedMessages.length - 1];
      if (
        latestMessage?.role === 'user' ||
        (latestMessage?.content?.length > 0 &&
          action.payload.message === 'Something went wrong. Please reach out to the admin.')
      ) {
        return;
      }
      state.messages = [
        ...updatedMessages.slice(0, -1),
        {
          ...latestMessage,
          content: action.payload.message,
          contentType: `${action.payload.chatKey || 'text'}`,
        },
      ];
    },
    setChatStreaming: (state, action) => {
      state.chatStreaming = action.payload;
      if (action.payload === false) {
        const updatedMessages = [...state.messages];
        if(updatedMessages.length === 0) return;
        const latestMessage = updatedMessages[updatedMessages.length - 1];
        if (latestMessage.role === 'user' || latestMessage.content.length > 0) {
          return;
        }
        state.messages = [
          ...updatedMessages.slice(0, -1),
          {
            ...latestMessage,
            content: 'Please try again.',
            contentType: 'text',
          },
        ];
      }
    },
    setCheckpoint: (state, action) => {
      state.checkpoint = action.payload;
    },
    appendSummarizationToChat: (state, action) => {
      const updatedMessages = [...state.messages];
      const latestMessage = updatedMessages[updatedMessages.length - 1];
      if (latestMessage.role === 'user') {
        return;
      }
      state.messages = [
        ...updatedMessages.slice(0, -1),
        {
          ...latestMessage,
          summarizedText: `${action.payload}`,
        },
      ];
    },
    appendExtraMessageDetailsToChat: (state, action) => {
      const updatedMessages = [...state.messages];
      const latestMessage = updatedMessages[updatedMessages.length - 1];
      if (latestMessage.role === 'user') {
        return;
      }
      state.messages = [
        ...updatedMessages.slice(0, -1),
        { ...latestMessage, content: `${latestMessage.content} ${action.payload}` },
      ];
    },
    setFetchState: (state, action) => {
      if (!action.payload) {
        state.checkpoint = '';
      }
    },
    removeLastMessage: (state) => {
      const updatedMessages = [...state.messages];
      const latestMessage = updatedMessages[updatedMessages.length - 1];
      if (!latestMessage?.content) {
        state.messages = [...updatedMessages.slice(0, -1)];
      }
    },
    setWelcomeVoice: (state) => {
      state.welcomeVoice = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getThreadId.pending, (state) => {
      state.loading = true;
      state.threadId = '';
      state.messages = [];
      state.chatStreaming = false;
      state.checkpoint = '';
      state.isAgentSpeaking = false;
      state.playbookInputFound = false;
      state.audioVisualizerOpen = false;
      state.welcomeVoice = false;
    });
    builder.addCase(getThreadId.fulfilled, (state, action) => {
      state.loading = false;
      state.threadId = action.payload;
      state.messages = [];
      state.chatStreaming = false;
      state.checkpoint = '';
      state.isAgentSpeaking = false;
      state.playbookInputFound = false;
      state.audioVisualizerOpen = false;
      state.welcomeVoice = false;
    });
    builder.addCase(getThreadId.rejected, (state) => {
      state.loading = true;
    });
  },
});
export const {
  setIsAgentSpeaking,
  toggleAudioVisualizer,
  setMessageGenerating,
  appendMessage,
  setChatStreaming,
  setCheckpoint,
  appendSummarizationToChat,
  appendExtraMessageDetailsToChat,
  setFetchState,
  addNewMessage,
  removeLastMessage,
  setWelcomeVoice,
} = SproutsAgentSlice.actions;
export default SproutsAgentSlice.reducer;
